import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { usarSessao } from "./ProvedorDeSessao";

/**
 * Barra a rota enquanto não há sessão completa.
 *
 * <p><b>Isto é experiência de uso, não segurança.</b> Toda requisição é
 * verificada no backend, independentemente do que a interface mostrou
 * (ADR-0003). Um usuário que forçasse a rota aqui veria uma tela vazia e
 * uma sequência de 401 — o que ele não veria é dado.
 *
 * <p>O caminho pretendido é guardado no `state` para que, depois de
 * entrar, a pessoa chegue onde queria — e não à tela inicial, tendo de
 * navegar de novo.
 *
 * <h2>O destino é o endereço INTEIRO, não só o caminho</h2>
 *
 * <p>Ele era `local.pathname`, e a consulta caía fora. Isso já era ruim
 * — `?empresa=…` perdido leva a pessoa a uma tela que pede de novo o que
 * ela já tinha escolhido — e virou defeito de verdade com o QR da
 * etiqueta: quem escaneia um tambor **sem sessão** precisa entrar e
 * aterrissar **naquele lote**. Cair no painel, depois de escanear, é o
 * mesmo que o QR não ter funcionado — com o agravante de a pessoa achar
 * que funcionou.
 */
export function RotaProtegida({ children }: { children: ReactNode }) {
  const { estado } = usarSessao();
  const local = useLocation();

  // `search` e `hash` viajam junto. `Navigate to` aceita a string inteira, e
  // é sempre um caminho interno — não há redirecionamento aberto aqui: o
  // valor vem do roteador, nunca de parâmetro que alguém possa fabricar.
  const destino = local.pathname + local.search + local.hash;

  if (estado.tipo === "verificando") {
    return (
      <div className="carregando" role="status">
        Carregando…
      </div>
    );
  }

  if (estado.tipo === "parcial") {
    // O destino atravessa o segundo fator também: sem isto, quem escaneia o
    // tambor, digita a senha e confirma o código de seis dígitos aterrissa no
    // painel — a meia-entrada perdendo o que a entrada inteira preservava.
    return <Navigate to="/entrar/codigo" replace state={{ de: destino }} />;
  }

  if (estado.tipo === "anonimo") {
    return <Navigate to="/entrar" replace state={{ de: destino }} />;
  }

  return <>{children}</>;
}
